import React from 'react';
import { AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react';

export const ErrorHandler = ({ error, onRetry }) => {
  if (!error) return null;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4" data-testid="error-handler">
      <div className="flex items-start gap-3">
        <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-red-800 font-medium">Error</h4>
          <p className="text-red-600 text-sm mt-1">
            {error.response?.data?.detail || error.message || 'An unexpected error occurred'}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 text-sm text-red-700 hover:text-red-800 underline"
              data-testid="error-retry-btn"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const SuccessMessage = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-testid="success-message">
      <div className="flex items-start gap-3">
        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
        <div className="flex-1">
          <p className="text-green-700">{message}</p>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-green-500 hover:text-green-700">
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export const InfoMessage = ({ message }) => {
  if (!message) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="info-message">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5" />
        <p className="text-blue-700">{message}</p>
      </div>
    </div>
  );
};

export const WarningMessage = ({ message }) => {
  if (!message) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="warning-message">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
        <p className="text-amber-700">{message}</p>
      </div>
    </div>
  );
};

export const LoadingSpinner = ({ size = 'md', text = 'Loading...' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className="flex flex-col items-center justify-center py-8" data-testid="loading-spinner">
      <div className={`${sizeClasses[size]} border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin`}></div>
      {text && <p className="mt-2 text-gray-500 text-sm">{text}</p>}
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, description, action }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4" data-testid="empty-state">
      {Icon && <Icon className="w-16 h-16 text-gray-300 mb-4" />}
      <h3 className="text-lg font-medium text-gray-900 mb-1">{title}</h3>
      {description && <p className="text-gray-500 text-center max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};

export default ErrorHandler;
